import React, { useState } from 'react';
import { FileText, Download, Eye, EyeOff, BookOpen } from 'lucide-react';
import PdfViewer, { resolveUrl } from './PdfViewer';

const DocumentViewerSection = ({ content }) => {
  const [previewOpen, setPreviewOpen] = useState(true);

  const data = {
    title: 'Download Document',
    subtitle: '',
    document_url: '',
    document_label: 'Document',
    enable_preview: true,
    enable_download: true,
    button_label: 'Download PDF',
    accent_color: '#00457f',
    ...content,
  };

  const docUrl = resolveUrl(data.document_url);
  const isPdf = docUrl.toLowerCase().endsWith('.pdf') || data.document_url?.includes('/api/media/file/');
  const isImage = /\.(jpe?g|png|gif|webp|svg)$/i.test(docUrl);

  if (!docUrl) return null;

  return (
    <section className="py-16 bg-gradient-to-b from-slate-50 to-white" data-testid="section-document-viewer">
      <div className="max-w-5xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-10">
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-5"
            style={{ backgroundColor: `${data.accent_color}0a`, color: data.accent_color }}
          >
            <BookOpen className="w-4 h-4" />
            <span className="text-xs font-bold uppercase tracking-widest">Document</span>
          </div>
          {data.title && (
            <h2 className="text-2xl md:text-4xl font-bold text-[#0a1a2f] tracking-tight">
              {data.title}
            </h2>
          )}
          {data.subtitle && (
            <p className="mt-3 text-base text-slate-600 max-w-2xl mx-auto leading-relaxed">
              {data.subtitle}
            </p>
          )}
        </div>

        {/* Document card */}
        <div className="rounded-2xl border border-slate-200/80 shadow-lg shadow-slate-200/50 overflow-hidden bg-white">
          {/* Top bar */}
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100 bg-white">
            <div className="flex items-center gap-3 min-w-0">
              <div
                className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                style={{ backgroundColor: `${data.accent_color}12`, color: data.accent_color }}
              >
                <FileText className="w-[18px] h-[18px]" />
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-sm text-slate-900 truncate">{data.document_label}</p>
                <p className="text-[11px] text-slate-400">{isPdf ? 'PDF Document' : isImage ? 'Image' : 'Document'}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {data.enable_preview && (isPdf || isImage) && (
                <button
                  onClick={() => setPreviewOpen(!previewOpen)}
                  className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 px-3 py-1.5 rounded-lg hover:bg-slate-50 transition-colors border border-transparent hover:border-slate-200"
                  data-testid="doc-toggle-preview"
                >
                  {previewOpen ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  {previewOpen ? 'Hide' : 'Show'} Preview
                </button>
              )}
              {data.enable_download && (
                <a
                  href={docUrl}
                  download={data.document_label || 'download'}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="doc-download-btn"
                  className="inline-flex items-center gap-2 text-white font-semibold text-sm px-5 py-2 rounded-xl transition-all hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]"
                  style={{ backgroundColor: data.accent_color }}
                >
                  <Download className="w-4 h-4" />
                  {data.button_label || 'Download'}
                </a>
              )}
            </div>
          </div>

          {/* Preview area */}
          {data.enable_preview && previewOpen && (
            <div data-testid="doc-preview-area">
              {isPdf ? (
                <PdfViewer
                  url={data.document_url}
                  label={data.document_label}
                  accentColor={data.accent_color}
                  showDownload={data.enable_download}
                  initialHeight={640}
                />
              ) : isImage ? (
                <div className="bg-gradient-to-b from-slate-100 to-slate-50 p-8 flex justify-center">
                  <img
                    src={docUrl}
                    alt={data.document_label || 'Document'}
                    className="max-w-full max-h-[70vh] object-contain rounded-xl shadow-lg"
                    data-testid="doc-image-preview"
                  />
                </div>
              ) : (
                <div className="p-12 text-center text-slate-500 bg-slate-50">
                  <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p className="font-medium">Preview not available for this file type.</p>
                  <p className="text-sm mt-1 text-slate-400">Click the download button to view the document.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default DocumentViewerSection;
